import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { BirdiTheme } from '@birdi/theme';
import UploadSVG from '@birdi/icons/upload.svg';
import TrashSVG from '@birdi/icons/trash-2.svg';

const dropzoneCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 9rem;
  padding: 1rem;
  background: ${theme.mono10};
  border-radius: 8px;
`;

const uploadButtonCSS = (theme: BirdiTheme) => css`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.1rem;
  background: ${theme.mono0};
  color: ${theme.deepOcean};
  border: 1px solid ${theme.mono30};
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(9, 30, 66, 0.08);
  svg {
    width: 1.1em;
    height: 1.1em;
  }
`;

const previewCSS = css`
  position: relative;
  width: 100%;
  img {
    display: block;
    width: 100%;
    max-height: 12rem;
    object-fit: cover;
    border-radius: 6px;
  }
`;

const deleteButtonCSS = (theme: BirdiTheme) => css`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid ${theme.mono30};
  border-radius: 6px;
  color: ${theme.crimsonSunbird};
  cursor: pointer;
  svg {
    width: 1.1em;
    height: 1.1em;
  }
`;

/**
 * The gray "Upload image" dropzone shared by the create-site modal and the
 * change-site-image modal: shows a newly-chosen file's preview, else the
 * existing `currentImageUrl` (with a delete bin), else an Upload button. The
 * parent owns the File and the current-image removal; this manages the
 * object-URL preview.
 */
export const SiteImageUpload = ({
  imageFile,
  onSelect,
  currentImageUrl,
  onRemoveCurrent,
}: {
  imageFile: File | null;
  onSelect: (file: File | null) => void;
  /** Existing site photo to show when no new file is selected. */
  currentImageUrl?: string | null;
  /** Called when the user deletes the existing photo (bin icon). */
  onRemoveCurrent?: () => void;
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!imageFile) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = window.URL.createObjectURL(imageFile);
    setPreviewUrl(url);
    return () => window.URL.revokeObjectURL(url);
  }, [imageFile]);

  const clear = () => {
    onSelect(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div css={dropzoneCSS}>
      {previewUrl ? (
        <div css={previewCSS}>
          <img src={previewUrl} alt="Site preview" />
          <button
            type="button"
            aria-label="Remove image"
            css={deleteButtonCSS}
            data-testid="create-site-image-remove"
            onClick={clear}
          >
            <TrashSVG />
          </button>
        </div>
      ) : currentImageUrl ? (
        <div css={previewCSS}>
          <img src={currentImageUrl} alt="Current site image" />
          <button
            type="button"
            aria-label="Delete current image"
            css={deleteButtonCSS}
            data-testid="change-site-image-remove-current"
            onClick={() => onRemoveCurrent?.()}
          >
            <TrashSVG />
          </button>
        </div>
      ) : (
        <button
          type="button"
          css={uploadButtonCSS}
          data-testid="create-site-image-button"
          onClick={() => fileInputRef.current?.click()}
        >
          <UploadSVG />
          Upload image
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        data-testid="create-site-image"
        css={css`
          display: none;
        `}
        onChange={(event) => onSelect(event.target.files?.[0] ?? null)}
      />
    </div>
  );
};
